/**
 * Modèle du graphique « trois sources » : un SCHÉMA DE PRINCIPE, pas des
 * données. Base 18 = les quelques rendez-vous d'aujourd'hui, tous apportés par
 * le bouche-à-oreille (proche de zéro : le cabinet en a peu). Google Ads
 * démarre au mois 1, le référencement au mois 2, et le volume qu'ils apportent
 * fait grossir le bouche-à-oreille ; au mois 12 le total vaut environ 385, dont
 * 30 % de bouche-à-oreille.
 *
 * Tout est exprimé dans le repère du SVG (viewBox W × H) : les bandes y sont
 * dessinées étirées, les repères HTML y sont placés en pourcentage.
 */

export const CHART_W = 1000;
export const CHART_H = 430;
export const T_MIN = 0;
export const T_MAX = 12;
export const ADS_LAUNCH = 1;
export const SEO_LAUNCH = 2;

const BASE = 18;
const WORD_END = 115;
const ADS_END = 130;
const SEO_END = 140;
const V_MAX = 420;
const TOP_PAD = 60; // place pour les étiquettes des jalons
const SCALE = (CHART_H - TOP_PAD) / V_MAX;
const SAMPLES = 161;

const round = (v: number) => Math.round(v * 10) / 10;

export const xOf = (t: number) => ((t - T_MIN) / (T_MAX - T_MIN)) * CHART_W;
export const yOf = (v: number) => CHART_H - v * SCALE;

/**
 * Bouche-à-oreille : part de presque rien et grossit avec le volume apporté
 * par la publicité puis le référencement (chaque client amène des avis et des
 * recommandations) : croissance régulière dès le lancement, qui s'accentue.
 */
export const wordTrend = (t: number) =>
  t <= ADS_LAUNCH ? BASE : BASE + (WORD_END - BASE) * Math.pow((t - ADS_LAUNCH) / (T_MAX - ADS_LAUNCH), 1.15);
/** … et des fluctuations irrégulières, proportionnelles à son niveau (une petite source tremble peu en valeur). */
const wordAmp = (t: number) => 3 + 0.12 * wordTrend(t);
const noise = (t: number) =>
  0.55 * Math.sin(2.3 * t + 1.3) + 0.3 * Math.sin(5.1 * t + 0.4) + 0.15 * Math.sin(10.7 * t + 2.2);
export const wordTop = (t: number) => wordTrend(t) + wordAmp(t) * noise(t);

/** Google Ads : monte vite dès le lancement, puis se stabilise. */
export const ads = (t: number) => (t <= ADS_LAUNCH ? 0 : ADS_END * (1 - Math.exp(-(t - ADS_LAUNCH) / 0.9)));

/** Référencement : démarre au mois 2, plat longtemps, puis décolle. */
const logistic = (t: number) => 1 / (1 + Math.exp(-(t - 7.4) / 1.3));
export const seo = (t: number) =>
  t <= SEO_LAUNCH ? 0 : (SEO_END * (logistic(t) - logistic(SEO_LAUNCH))) / (logistic(T_MAX) - logistic(SEO_LAUNCH));

/**
 * Haut de la bande Google Ads. Tant qu'elle est mince, il épouse le
 * bouche-à-oreille (pas d'aplat dans ses creux) ; dès qu'elle prend du volume,
 * il devient lisse et c'est elle qui absorbe les creux : seule la frontière du
 * bas tremble.
 */
export const adsTop = (t: number) => {
  const k = Math.exp(-ads(t) / 10);
  return wordTrend(t) + ads(t) + (wordTop(t) - wordTrend(t)) * k;
};
export const seoTop = (t: number) => adsTop(t) + seo(t);
export const total = (t: number) => wordTrend(t) + ads(t) + seo(t);

type Point = readonly [number, number];

const times = Array.from({ length: SAMPLES }, (_, i) => T_MIN + (i / (SAMPLES - 1)) * (T_MAX - T_MIN));
const polyline = (points: ReadonlyArray<Point>) =>
  points.map(([x, y], i) => `${i ? "L" : "M"}${round(x)},${round(y)}`).join("");
const area = (upper: ReadonlyArray<Point>, lower: ReadonlyArray<Point>) =>
  `${polyline(upper)}${[...lower].reverse().map(([x, y]) => `L${round(x)},${round(y)}`).join("")}Z`;
const sample = (f: (t: number) => number): Point[] => times.map((t) => [xOf(t), yOf(f(t))]);

/** Les quatre tracés du graphique, en coordonnées du viewBox. */
export function buildSourcesPaths() {
  const word = sample(wordTop);
  const adsUpper = sample(adsTop);
  const adsLower = sample((t) => Math.min(wordTop(t), adsTop(t)));
  const floor: Point[] = times.map((t) => [xOf(t), CHART_H]);
  const seoUpper = sample(seoTop);
  const from = (t0: number) => (points: ReadonlyArray<Point>) => points.filter((_, i) => times[i] >= t0);
  return {
    word: area(word, floor),
    wordEdge: polyline(word),
    ads: area(adsUpper, adsLower),
    seo: area(seoUpper, adsUpper),
    // contours tracés au crayon (variante croquis), chacun depuis son lancement
    adsEdge: polyline(from(ADS_LAUNCH)(adsUpper)),
    seoEdge: polyline(from(SEO_LAUNCH)(seoUpper)),
  };
}

/**
 * Repères de progression, en multiples des rendez-vous d'aujourd'hui. Avec une
 * base proche de zéro, des pourcentages n'ont plus de sens (+1 500 %) : ce
 * sont des ordres de grandeur, et ils se lisent comme une promesse.
 */
export const SOURCES_LEVELS = [
  { value: BASE, label: "Aujourd'hui", base: true },
  { value: BASE * 5, label: "×5", base: false },
  { value: BASE * 10, label: "×10", base: false },
  { value: BASE * 20, label: "×20", base: false },
] as const;

/** Pourcentage de largeur d'un mois, pourcentage de hauteur d'une valeur. */
export const leftPct = (t: number) => `${round((xOf(t) / CHART_W) * 100)}%`;
export const topPct = (v: number) => `${round((yOf(v) / CHART_H) * 100)}%`;

/** Hauteur (en % du graphique) du milieu de chaque bande au mois 12, pour y poser son nom. */
export function endLabelTops() {
  const w = wordTrend(T_MAX);
  const a = adsTop(T_MAX);
  const s = seoTop(T_MAX);
  return { word: topPct(w / 2), ads: topPct((w + a) / 2), seo: topPct((a + s) / 2) };
}

/** Point d'ancrage d'une flèche : la courbe et un point juste après, pour la pente. */
export type ArrowAnchor = { x0: number; y0: number; x1: number; y1: number };
export function arrowAnchor(curve: "ads" | "seo", t: number): ArrowAnchor {
  const f = curve === "ads" ? adsTop : seoTop;
  return { x0: xOf(t), y0: yOf(f(t)), x1: xOf(t + 0.05), y1: yOf(f(t + 0.05)) };
}

/**
 * Géométrie d'une flèche courte en pixels réels : posée sur la tangente de la
 * courbe, décalée au-dessus d'elle. Calculée hors du SVG étiré, sinon la
 * pointe serait déformée.
 */
export function arrowGeometry(anchor: ArrowAnchor, width: number, height: number, small: boolean) {
  const sx = width / CHART_W;
  const sy = height / CHART_H;
  const len = small ? 26 : 42;
  const gap = small ? 8 : 14;
  const head = small ? 5 : 7;
  const x0 = anchor.x0 * sx;
  const y0 = anchor.y0 * sy;
  const dx = anchor.x1 * sx - x0;
  const dy = anchor.y1 * sy - y0;
  const k = Math.hypot(dx, dy) || 1;
  const ux = dx / k;
  const uy = dy / k;
  // la normale (uy, -ux) pointe vers le haut quand la courbe monte vers la droite
  const startX = x0 + uy * gap - (ux * len) / 2;
  const startY = y0 - ux * gap - (uy * len) / 2;
  const endX = startX + ux * len;
  const endY = startY + uy * len;
  const angle = Math.atan2(uy, ux);
  const wing = (spread: number) =>
    `${round(endX - head * Math.cos(angle + spread))},${round(endY - head * Math.sin(angle + spread))}`;
  return {
    line: `M${round(startX)},${round(startY)}L${round(endX)},${round(endY)}`,
    head: `M${wing(0.6)}L${round(endX)},${round(endY)}L${wing(-0.6)}`,
    labelLeft: round(startX),
    labelTop: round(Math.min(startY, endY) - 6),
  };
}
